import { Settings } from "../../Settings";
import { SpecialTileTypes } from "../grid/TileTypes";
import { LayerDescriptionArray, LayerDescriptions } from "../grid/LayerDescriptions";
import Sprite from "../sprite/Sprite";
import Grid from "../grid/Grid";
import GridInversionOfControl from "../grid/GridInversionOfControl";
import GridTile from "../grid/GridTile";
import Vector from "../math/Vector";
import FractionalBrownianMotion from "./FractionalBrownianMotion";
import TerrainLayer from "./TerrainLayer";
import BasicTerrainLayer from "./BasicTerrainLayer";
import SkyTerrainLayer from "./SkyTerrainLayer";
import TileTypeBackground from "../grid/TileTypeBackground";
import GrassTerrainLayer from "./GrassTerrainLayer";
import DirtTerrainLayer from "./DirtTerrainLayer";
import MersenneTwister from "../math/MersenneTwister";
import IndexGenerator from "./IndexGenerator";
import TerrainLayerDecoratorConfig from "./decorator/TerrainLayerDecoratorConfig";
import CharacterCreator from "../character/CharacterCreator";
import { TerrainGenerator } from "./TerrainGenerator";

export default class LayeredTerrainGenerator extends TerrainGenerator {
    /**
     * @param {number} worldSeed 
     * @param {CharacterCreator} characterCreator
     */
    constructor(worldSeed, characterCreator) {
        super();
        
        let terrainSeed = worldSeed;

        /**
         * @constant
         * @type {FractionalBrownianMotion}
         */
        this.cavernFbmA = new FractionalBrownianMotion(terrainSeed++, 0.9, 2, 1.2, 5, 0.00041);

        /**
         * @type {number}
         */
        this.cavernValueAMin = -6;
        /**
         * @type {number}
         */
        this.cavernValueAMax = -1.3;

        /**
         * @constant
         * @type {FractionalBrownianMotion}
         */
        this.cavernFbmB = new FractionalBrownianMotion(terrainSeed++, 1.2, 1.25, 0.9, 5, 0.00261);
        /**
         * @type {number}
         */
        this.cavernValueBMin = 0.8;
        /**
         * @type {number}
         */
        this.cavernValueBMax = 4.1;

        /**
         * @constant
         * @type {FractionalBrownianMotion}
         */
        this.oreFbm = new FractionalBrownianMotion(terrainSeed++, 1, 1.5, 1.4, 5, 0.002);
        /**
         * @constant
         * @type {FractionalBrownianMotion}
         */
        this.pointsFbm = new FractionalBrownianMotion(terrainSeed++, 1, 1.6, 1.3, 5, 0.001);
        /**
         * @constant
         * @type {FractionalBrownianMotion}
         */
        this.upgradeFbm = new FractionalBrownianMotion(terrainSeed++, 1, 1.6, 1.4, 5, 0.001);
        /**
         * @constant
         * @type {FractionalBrownianMotion}
         */
        this.rubyFbm = new FractionalBrownianMotion(terrainSeed++, 1, 1.6, 1.4, 5, 0.001);

        /**
         * @constant
         * @type {FractionalBrownianMotion}
         */
        this.backgroundFbm = new FractionalBrownianMotion(terrainSeed++, 1, 2.1, 0.7, 5, 0.0005); 

        /**
         * @constant
         * @type {FractionalBrownianMotion}
         */
        const grassAndDirtFbm = new FractionalBrownianMotion(terrainSeed++, 0.7, 3.5, 0.8, 5, 0.00001);

        /**
         * @constant
         * @type {IndexGenerator}
         */
        this.indexGenerator = new IndexGenerator(worldSeed); // <-- Super heavy object.  Only 1 instance please.

        /**
         * @constant
         * @type {CharacterCreator}
         */
        this.characterCreator = characterCreator;

        /**
         * @constant
         * @type {Array.<TerrainLayer>}
         */
        this.terrainLayers = [];

        // Sky, Grass and Dirt are special layers. All the rest are just normal layers.
        for (let i = 0; i < LayerDescriptionArray.length; i++) {
            const layerDescription = LayerDescriptionArray[i];

            if (layerDescription === LayerDescriptions.SKY_LAYER) {
                this.terrainLayers.push(new SkyTerrainLayer(SpecialTileTypes.SKY, layerDescription));
            }
            else if (layerDescription === LayerDescriptions.GRASS_LAYER) {
                this.terrainLayers.push(new GrassTerrainLayer(layerDescription, grassAndDirtFbm)); // grass and dirt share fbm
            }
            else if (layerDescription === LayerDescriptions.DIRT_LAYER) {
                this.terrainLayers.push(new DirtTerrainLayer(layerDescription, grassAndDirtFbm)); // grass and dirt share fbm
            }
            else {
                const goldGenerated = i > 10; // 0,1,2 (air,grass,dirt) + 7 layers.
                const rubyGenerated = i > 20; // gold layer + 10 layers.
                this.terrainLayers.push(new BasicTerrainLayer(layerDescription, new FractionalBrownianMotion(terrainSeed++, 1, 3.5, 0.9, 5, 0.00005), goldGenerated, rubyGenerated));
            }
        }

        this.terrainLayerById = {};

        for (let i = 0; i < this.terrainLayers.length; i++) {
            const layer = this.terrainLayers[i];
            const layerDescription = layer.getLayerDescription();
            if (!layerDescription) {
                console.log('LayeredTerrainGenerator No layer description for layer index: ' + i);
                continue;
            }

            this.terrainLayerById[layerDescription.id] = layer;
        }

        /**
         * @constant
         */
        this.cavernDecoratorsByLayer = {            
        };

        /**
         * @constant
         * @type {TerrainLayerDecoratorConfig}
         */
        this.layerDecoratorConfig = new TerrainLayerDecoratorConfig();

        /**
         * @constant
         * @type {MersenneTwister}
         */
        this.rng = new MersenneTwister(terrainSeed); // This seed doesn't matter really.

        for (let i = 0; i < LayerDescriptionArray.length; i++) {
            const layerDescription = LayerDescriptionArray[i];

            // Skip the SKY
            if (layerDescription === LayerDescriptions.SKY_LAYER) {
                continue;
            }

            // The grass is assigned an empty decorator.
            if (layerDescription === LayerDescriptions.GRASS_LAYER) {
                this.cavernDecoratorsByLayer[layerDescription.id] = [];
            }
            else {
                this.cavernDecoratorsByLayer[layerDescription.id] = this.layerDecoratorConfig.getRandomLayerDecorator(this.rng);
            }
        }

    }

    /**
     * @override
     * @param {Grid} grid 
     */
    generateTerrain(grid) {

        // Find the subset of tile layers that could possibly be applied to this grid.
        const gridTerrainLayers = this.findApplicableTerrainLayersForGrid(grid);

        // Assign terrain to every tile in the grid.

        const zones = grid.zones;
        for (let i = 0; i < zones.length; i++) {
            const zone = zones[i];
            const regions = zone.regions;
            for (let r = 0; r < regions.length; r++) {
                const tiles = regions[r].tiles;
                for (let t = 0; t < tiles.length; t++) {
                    this.assignTileTerrain(tiles[t], gridTerrainLayers);
                }
            }
        }

        // Do a second pass to decorate the caverns.
        for (let i = 0; i < zones.length; i++) {
            const zone = zones[i];
            const regions = zone.regions;
            for (let r = 0; r < regions.length; r++) {
                const tiles = regions[r].tiles;
                for (let t = 0; t < tiles.length; t++) {
                    const tile = tiles[t];
                    if (!tile.open || tile.fixtureDescription) {
                        continue;
                    }

                    this.assignCavernDecorationToTile(tile);
                }
            }
        }
    }

    /**
     * When we load a grid from the save file, we need to generate the background,
     * because that isn't in the save file but can be derived form the tile types.
     * @param {Grid} grid 
     */
    generateTileBackgrounds(grid) {
        this.generateBackground(grid);
    }

    /**
     * @param {GridTile} tile
     * @returns {boolean}
     */
    isTileCavern(tile) {

        const tileType = tile.tileType;
        if (!tileType) {
            console.log('LayeredTerrainGenerator.isTileCavern() no tile type');
            return;
        }

        const layerDescription = tileType.layerDescription;
        if (!layerDescription) {
            console.log('LayeredTerrainGenerator.isTileCavern() no layer desc. tileType.icon=' + tileType.iconFileName);
            return;
        }

        /**
         * @type {BasicTerrainLayer}
         */
        const terrainLayer = this.terrainLayerById[layerDescription.id];
        if (!terrainLayer) {
            console.log('LayeredTerrainGenerator.isTileCavern() no terrain layer. id=' + layerDescription.id);
            return;
        }

        const origin = tile.origin;
        // const tileRowDepth = tile.getTileRow();

        // // Don't put caverns super close to the surface
        // if (tileRowDepth < 5) {
        //     return;
        // }
        // else if (tileRowDepth < 10 && this.rng.random() < 0.6) {
        //     return;
        // }

        // Find the distance to the edge of the current terrain layer
        // const dist = Math.abs(tileRowDepth - terrainLayer.centerDepth);
        // const edgeThreshold = terrainLayer.distFromCenter;

        // Make an attempt to reduce the number of caverns that span terrain layer boundaries.
        //const penaltyMultiplier = dist < edgeThreshold ? 1 : (1.0 - ((dist - edgeThreshold) / edgeThreshold));
        //const penaltyMultiplier = dist < 80 ? 1 : (1.0 - ((dist - 80) / 80));
        /////const penaltyMultiplier = dist < 5 ? 1 : (1.0 - ((dist - 5) / 5));

        // console.log('depth=' + tileRowDepth + ' centerDepth=' + terrainLayer.centerDepth + ' layer=' + terrainLayer.layerDescription.primaryTileType.iconFileName + ' dist=' + dist + ' halfThick=' + edgeThreshold + ' penalty=' + penaltyMultiplier);

        // 1
        // const cavernValueA = penaltyMultiplier * this.cavernFbmA.calculateNoise(origin.y, origin.x); // swapped x/y
        // if (cavernValueA > 2.4) {
        //     return true;
        // }

        // 2
        // Perlin Worms 1:
        // const cavernValueA = penaltyMultiplier * this.cavernFbmA.calculateNoise(origin.x, origin.y * 2); // swapped x/y
        // if (cavernValueA > 1.3 && cavernValueA < 2.7) {
        //     return true;
        // }


        // Perlin Worms: Value must be within a range
        // orig 1
        //const cavernValueB = penaltyMultiplier * this.cavernFbmB.calculateNoise(origin.x / 2, origin.y); orig
        //return cavernValueB > 1.8 && cavernValueB < 2.5;

        // take 2
        // const cavernValueB = penaltyMultiplier * this.cavernFbmB.calculateNoise(origin.x / 2, origin.y * 2);
        // return cavernValueB > 1.8 && cavernValueB < 2.5;

        //return false;

        // https://accidentalnoise.sourceforge.net/minecraftworlds.html
        
        //const cavernValueA = penaltyMultiplier * this.cavernFbmA.calculateNoise(origin.x, origin.y);
        const cavernValueA = this.cavernFbmA.calculateNoise(origin.x, origin.y);
        if (cavernValueA > this.cavernValueAMin && cavernValueA < this.cavernValueAMax) {
            return true;
        }

        //const cavernValueB = penaltyMultiplier * this.cavernFbmB.calculateNoise(origin.x / 2, origin.y * 2);
        const cavernValueB = this.cavernFbmB.calculateNoise(origin.x / 2, origin.y * 2);
        return cavernValueB > this.cavernValueBMin && cavernValueB < this.cavernValueBMax;

        // return false;
    }


    /////////////////////////////////////////
    // Tile Background
    /////////////////////////////////////////

    /**
     * @private
     * @param {Grid} grid 
     */
    generateBackground(grid) {
        const thisRef = this;

        GridInversionOfControl.allTilesInGrid(grid, 
            /** 
             * @param {GridTile} tile 
             */ 
            function(tile) {
                thisRef.assignTileBackgroundSprite(tile);

                //tile.open = true; // <-- just for testing the background
            }
        );
    }

    /**
     * Assign a background sprite to the tile based on the primary tile type of the terrain layer that the tile is associated with.
     * @private
     * @param {GridTile} tile 
     */
    assignTileBackgroundSprite(tile) {
        const tileType = tile.tileType;
        if (!tileType) {
            console.log("LayeredTerrainGenerator.setTileBackground() ERROR Tile has no TileType!");
            return;
        }

        // Note: the tile type can be EMPTY, which has no layerDescription or background.

        // Use the background of the primary tile type of the layer.
        const layerDescription = tileType.layerDescription;
        if (layerDescription) {
            tile.backgroundSprite = this.getBackgroundSprite(tile.origin, layerDescription.primaryTileType.tileTypeBackground);
        }
        else if (tileType.tileTypeBackground) {
            tile.backgroundSprite = this.getBackgroundSprite(tile.origin, tileType.tileTypeBackground);
        }
    }

    /**
     * @private
     * @param {Vector} origin 
     * @param {TileTypeBackground} tileTypeBackground
     * @returns {Sprite}
     */
    getBackgroundSprite(origin, tileTypeBackground) {
        const value = Math.abs(this.backgroundFbm.calculateNoise(origin.x, origin.y) + 1); 

        // range: 0 to 3.25
        // get x in the range of [0..1]
        const x = Math.min(Math.max(0, value / 3.25), 1); // <-- 3.25 is roughly the max value

        const index = (x * (tileTypeBackground.sprites.length - 1)) | 0;

        return tileTypeBackground.sprites[index];
    }

    /////////////////////////////////////////
    // Cavern Decoration
    /////////////////////////////////////////

    /**
     * @private
     * @param {GridTile} tile 
     */
    assignCavernDecorationToTile(tile) {
        // We can navigate from the tile to the terrain layer.
        const tileType = tile.tileType;
        if (!tileType) {
            return;
        }

        const layerDescription = tileType.layerDescription;
        if (!layerDescription) {
            return;
        }

        const cavernDecorators = this.cavernDecoratorsByLayer[layerDescription.id];
        if (!cavernDecorators || cavernDecorators.length === 0) {
            return;
        }

        // Use FBM to choose the cavern decorator for this tile.
        const origin = tile.origin;
        const cavernDecorator = cavernDecorators[this.indexGenerator.getIndexFromFbm(origin.x, origin.y, cavernDecorators.length)];

        cavernDecorator.decorateCavernTile(tile, this.rng);
        
        /////////////////////////////////////
        // Monster Creation Stuff
        /////////////////////////////////////

        // 1% chance of adding a monster to the tile.
        if (layerDescription.monsterDescriptions.length > 0 && this.rng.random() < 0.01) {
            const monsterDescription = layerDescription.monsterDescriptions[this.rng.randomInt(layerDescription.monsterDescriptions.length)];
            this.characterCreator.createMonsterAtTile(monsterDescription, tile);
        }
    }


    /////////////////////////////////////////
    // Tile Terrain
    /////////////////////////////////////////

    /**
     * @private
     * @param {GridTile} tile 
     * @param {Array.<TerrainLayer>} gridTerrainLayers
     */
    assignTileTerrain(tile, gridTerrainLayers) {

        const terrainLayer = this.findTerrainLayerForTile(tile, gridTerrainLayers);
        if (!terrainLayer) {
            return;
        }

        // Give the tile the default tile type for the terrain layer.
        terrainLayer.assignTileTerrain(tile, false);

        ///////////////////////////////////////////
        // Cavern or Not Cavern
        ///////////////////////////////////////////

        if (this.isTileCavern(tile)) {
            // This is a cavern tile. Make tile open.
            tile.open = true;
        }
        else {
            // This is a regular non-cavern tile.
            this.assignTypeForNonCavernTile(tile, terrainLayer);
        }

        ///////////////////////////////////////////
        // Tile Background
        ///////////////////////////////////////////

        this.assignTileBackgroundSprite(tile);
    }

    /**
     * @private
     * @param {GridTile} tile 
     * @param {TerrainLayer} terrainLayer 
     */
    assignTypeForNonCavernTile(tile, terrainLayer) {
        const origin = tile.origin;

        const rand = this.rng.random();

        // 80% chance of ore tile evaluation
        if (rand < 0.8 && terrainLayer.isOreTileSupported()) {
            const lesserValue = this.oreFbm.calculateNoise(origin.y, origin.x / 3); 
            if (lesserValue < -3.55) { // this was 3
                terrainLayer.assignTileToOreType(tile);
                return;
            }
        }

        // 40% chance of GOLD tile eval
        if (rand < 0.4 && terrainLayer.isGoldTileSupported()) {
            const greaterValue = this.pointsFbm.calculateNoise(origin.x, origin.y);
            if (greaterValue < -3.5) { // this was 3
                terrainLayer.assignTileToGoldType(tile);
                return;
            }
        }

        // 2% chance for UPGRADE tile eval
        if (rand < 0.02 && terrainLayer.isUpgradeTileSupported()) {
            const rareValue = this.upgradeFbm.calculateNoise(origin.x, origin.y);
            if (rareValue < -3) {
                terrainLayer.assignTileToUpgradeType(tile);
                return;
            }
        }

        // 1% chance of RUBY tile eval
        if (rand < 0.01 && terrainLayer.isRubyTileSupported()) {
            const greaterValue = this.rubyFbm.calculateNoise(origin.x, origin.y);
            if (greaterValue < -3) {
                terrainLayer.assignTileToRubyType(tile);
                return;
            }
        }
    }

    /**
     * Finds the subset of the tile layers that could possibly be applied to the grid tiles.
     * @private
     * @param {Grid} grid 
     * @returns {Array.<TerrainLayer>}
     */
    findApplicableTerrainLayersForGrid(grid) {
        const minGridTileDepth = grid.origin.y / Settings.tile.size;
        const maxGridTileDepth = minGridTileDepth + Settings.grid.gridTileHeight;

        const gridLayers = [];

        for (let i = 0; i < this.terrainLayers.length; i++) {
            const layer = this.terrainLayers[i];

            if (layer.isLayerForGrid(minGridTileDepth, maxGridTileDepth)) {
                gridLayers.push(layer);
            }
        }

        return gridLayers;
    }

    /**
     * @private
     * @param {GridTile} tile 
     * @param {Array.<TerrainLayer>} gridTileLayers
     * @returns {TerrainLayer}
     */
    findTerrainLayerForTile(tile, gridTileLayers) {
        const tileDepth = tile.origin.y / Settings.tile.size;

        for (let i = gridTileLayers.length - 1; i >= 0; i--) {
            const layer = gridTileLayers[i];
            if (layer.isLayerForTile(tile, tileDepth)) {
                return layer;
            }
        }

        // If we get to this point, then default to the first tile layer
        if (gridTileLayers.length > 0) {
            return gridTileLayers[0];
        }

        return null;
    }
}